import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../utils/api';

function AssistantChatWidget({ roleHint }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'assistant', text: 'Hi! 👋 I\'m here to help you with the rental platform. Ask me anything!' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const [aiHealthy, setAiHealthy] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [faqs, setFaqs] = useState([]);
  
  // Draggable button state - store position as percentage of viewport
  const [buttonPosition, setButtonPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('assistantButtonPosition');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (!parsed || typeof parsed !== 'object') {
          return { side: 'right', yPercent: 0.85 };
        }
        // If old format (absolute pixels), convert to percentage
        if (parsed.y > 1) {
          return { side: parsed.side || 'right', yPercent: 0.85 };
        }
        return {
          side: parsed.side === 'left' ? 'left' : 'right',
          yPercent: Number.isFinite(Number(parsed.yPercent)) ? Number(parsed.yPercent) : 0.85
        };
      }
    } catch (_) {
      try { localStorage.removeItem('assistantButtonPosition'); } catch (_) {}
    }
    return { side: 'right', yPercent: 0.85 }; // 85% down the screen by default
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const buttonRef = useRef(null);

  const roleMap = {
    tenant: 'T',
    landlord: 'L',
    manager: 'M',
    admin: 'A',
    member: 'Mbr'
  };

  const currentRole = roleMap[(roleHint || userRole || '').toLowerCase()] || (userRole || null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await api.get('/assistant/health');
        setAiHealthy(!!res.data?.openai_key_present);
      } catch (_) {
        setAiHealthy(false);
      }
    };
    const loadUser = async () => {
      try {
        const res = await api.get('/auth/me');
        const role = res.data?.role || null;
        setUserRole(role);
        // Only load FAQs if user is authenticated
        if (role) {
          loadFaqs();
        }
      } catch (_) {
        setUserRole(null);
      }
    };
    const loadFaqs = async () => {
      try {
        const res = await api.get('/assistant/faqs');
        if (Array.isArray(res.data?.faqs)) setFaqs(res.data.faqs);
      } catch (_) {}
    };
    checkHealth();
    loadUser();
  }, []);

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setMessages(prev => [...prev, { from: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/assistant/query', { role: currentRole, question: q });
      const answer = res.data?.answer || 'No answer.';
      setMessages(prev => [...prev, { from: 'assistant', text: answer }]);
    } catch (e) {
      const errMsg = e?.response?.data?.error || e?.message || 'Unknown error';
      setMessages(prev => [...prev, { from: 'assistant', text: `Error: ${errMsg}` }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const sendQuick = (q) => {
    setInput(q);
    setTimeout(() => send(), 0);
  };

  // Drag handlers for the button
  const handleMouseDown = (e) => {
    if (open) return; // Don't drag when chat is open
    e.preventDefault(); // Prevent text selection during drag
    setIsDragging(true);
    setHasMoved(false);
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setHasMoved(true); // Mark that we've moved
    
    const buttonHeight = buttonRef.current?.offsetHeight || 60;
    const newY = Math.max(10, Math.min(window.innerHeight - buttonHeight - 10, e.clientY - dragOffset.y));
    const yPercent = newY / window.innerHeight;
    
    setButtonPosition(prev => ({ ...prev, yPercent }));
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (hasMoved) {
      // Snap to nearest side (left or right) only if moved
      const middleX = window.innerWidth / 2;
      const newSide = e.clientX < middleX ? 'left' : 'right';
      
      const newPosition = { ...buttonPosition, side: newSide };
      setButtonPosition(newPosition);
      localStorage.setItem('assistantButtonPosition', JSON.stringify(newPosition));
    }
    
    // Reset hasMoved after a short delay to prevent click
    setTimeout(() => setHasMoved(false), 100);
  };

  const handleButtonClick = (e) => {
    // Only open if we didn't just drag
    if (!hasMoved) {
      setOpen(true);
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (open) return;
    e.preventDefault();
    setIsDragging(true);
    setHasMoved(false);
    const touch = e.touches[0];
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setHasMoved(true);
    
    const touch = e.touches[0];
    const buttonHeight = buttonRef.current?.offsetHeight || 60;
    const newY = Math.max(10, Math.min(window.innerHeight - buttonHeight - 10, touch.clientY - dragOffset.y));
    const yPercent = newY / window.innerHeight;
    
    setButtonPosition(prev => ({ ...prev, yPercent }));
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (hasMoved) {
      const touch = e.changedTouches[0];
      const middleX = window.innerWidth / 2;
      const newSide = touch.clientX < middleX ? 'left' : 'right';
      
      const newPosition = { ...buttonPosition, side: newSide };
      setButtonPosition(newPosition);
      localStorage.setItem('assistantButtonPosition', JSON.stringify(newPosition));
    }
    
    setTimeout(() => setHasMoved(false), 100);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent scrolling while dragging
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.overflow = '';
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  // Handle window resize to keep button proportional
  useEffect(() => {
    const handleResize = () => {
      // Force re-render with new viewport dimensions
      setButtonPosition(prev => ({ ...prev }));
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate button position styles - convert percentage to pixels
  const buttonHeight = 60; // approximate button height
  const minY = 10;
  const maxY = window.innerHeight - buttonHeight - 10;
  const calculatedY = buttonPosition.yPercent * window.innerHeight;
  const actualY = Math.max(minY, Math.min(maxY, calculatedY));
  
  const buttonStyle = {
    position: 'fixed',
    top: actualY,
    [buttonPosition.side]: 16,
    zIndex: 1000,
    cursor: isDragging ? 'grabbing' : 'grab',
    transition: isDragging ? 'none' : 'all 0.3s ease'
  };

  // Chat widget always appears in bottom-right
  const chatStyle = {
    position: 'fixed',
    right: 16,
    bottom: 16,
    zIndex: 1000
  };

  return (
    <>
      {!open && (
        <button
          ref={buttonRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleButtonClick}
          style={buttonStyle}
          className="bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 px-4 py-3 select-none"
          aria-label="Open assistant chat"
        >
          💬
        </button>
      )}
      {open && (
        <div style={chatStyle}>
        <div className="bg-white w-80 sm:w-96 rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden max-h-[85vh]">
          <div className="bg-green-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="font-semibold" aria-label="Assistant">💬</div>
            <button onClick={() => setOpen(false)} className="text-white/90 hover:text-white">✕</button>
          </div>
          <div className="bg-yellow-50 text-yellow-800 text-xs px-3 py-2 border-b border-yellow-200">
            The assistant may be inaccurate or incomplete. Do not rely on it for decisions.
            {!aiHealthy && ' (AI key not configured; using built-in help only)'}
          </div>
          {faqs.length > 0 && (
            <div className="px-3 py-2 border-b border-gray-200 overflow-x-auto">
              <div className="flex gap-2 whitespace-nowrap">
                {faqs.slice(0, 8).map((f, i) => (
                  <button
                    key={i}
                    onClick={() => sendQuick(f.q)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded flex-shrink-0"
                  >{f.q}</button>
                ))}
              </div>
            </div>
          )}
          <div className="p-3 flex-1 overflow-y-auto space-y-2 text-sm min-h-0">
            {messages.map((m, idx) => (
              <div key={idx} className={m.from === 'user' ? 'text-right' : 'text-left'}>
                <div className={
                  'inline-block px-3 py-2 rounded-lg max-w-[85%] ' +
                  (m.from === 'user' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-800')
                }>
                  {m.from === 'user' ? (
                    m.text
                  ) : (
                    <div className="markdown-content">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                          li: ({node, ...props}) => <li className="mb-1" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
                          em: ({node, ...props}) => <em className="italic" {...props} />,
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2" {...props} />,
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2" {...props} />,
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-1" {...props} />,
                          code: ({node, inline, ...props}) => 
                            inline ? (
                              <code className="bg-gray-200 px-1 rounded text-xs" {...props} />
                            ) : (
                              <code className="block bg-gray-200 p-2 rounded text-xs my-2" {...props} />
                            )
                        }}
                      >
                        {m.text}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-left">
                <div className="inline-block px-3 py-2 rounded-lg bg-gray-100 text-gray-500">Typing…</div>
              </div>
            )}
          </div>
          <div className="border-t border-gray-200 p-2 flex items-center gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Ask a question…"
            />
            <button
              onClick={send}
              disabled={loading}
              className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >Send</button>
          </div>
        </div>
        </div>
      )}
    </>
  );
}

export default AssistantChatWidget;


